import React, { useEffect, useState, useRef } from "react";
import {
  StyleSheet,
  ScrollView,
  Pressable,
  Text,
  TextInput,
  ActivityIndicator,
  useColorScheme,
  Image,
  Modal,
} from "react-native";
import Alert from "../components/Alert";
import { View } from "../components/Themed";
import { useQuery, gql, useMutation } from "@apollo/client";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as ImagePicker from "expo-image-picker";
import Config from "../constants/Config";
import { MaterialIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { getProductImageUrl } from "../utils/urls";
import { v4 as uuidv4 } from "uuid";
import localization from "../utils/localization";

const PRODUCTS_AND_CATEGORIES_QUERY = gql`
  query getProductsAndCategories {
    categories(token: "token") {
      id
      name
      order
    }
    products(token: "token") {
      id
      category
      title
      subtitle
      imageUrls
      published
      available
      variants
      volumes
      capabilities
      capabilityData
      allergens
      prices
      order
      options {
        id
        type
        title
        required
        options {
          id
          title
          prices
        }
      }
    }
  }
`;

const CREATE_PRODUCT_MUTATION = gql`
  mutation createProduct(
    $token: String!
    $operator: ID!
    $id: ID!
    $category: ID!
    $title: String!
    $subtitle: String!
    $imageUrls: [String!]!
    $published: Boolean!
    $available: Boolean!
    $variants: [String!]!
    $volumes: [String]
    $capabilities: [String!]!
    $capabilityData: [String]!
    $allergens: [[String!]!]
    $prices: [Float!]!
    $options: [OptionInput]
    $order: Int
  ) {
    createProduct(
      token: $token
      operator: $operator
      id: $id
      category: $category
      title: $title
      subtitle: $subtitle
      imageUrls: $imageUrls
      published: $published
      available: $available
      variants: $variants
      volumes: $volumes
      capabilities: $capabilities
      capabilityData: $capabilityData
      allergens: $allergens
      prices: $prices
      options: $options
      order: $order
    ) {
      id
      title
      category
    }
  }
`;

const UPDATE_PRODUCT_MUTATION = gql`
  mutation updateProduct(
    $token: String!
    $operator: ID!
    $id: ID!
    $title: String
    $subtitle: String
    $category: ID
    $published: Boolean
    $available: Boolean
    $variants: [String!]
    $volumes: [String]
    $allergens: [[String!]!]
    $prices: [Float!]
    $options: [OptionInput]
    $order: Int
  ) {
    updateProduct(
      token: $token
      operator: $operator
      id: $id
      title: $title
      subtitle: $subtitle
      category: $category
      published: $published
      available: $available
      variants: $variants
      volumes: $volumes
      allergens: $allergens
      prices: $prices
      options: $options
      order: $order
    ) {
      id
      title
    }
  }
`;

const DELETE_PRODUCT_MUTATION = gql`
  mutation deleteProduct($token: String!, $operator: ID!, $id: ID!) {
    deleteProduct(token: $token, operator: $operator, id: $id)
  }
`;

const UPLOAD_PRODUCT_IMAGE_MUTATION = gql`
  mutation uploadProductImage($token: String!, $operator: ID!, $productId: ID!, $file: Upload!) {
    uploadProductImage(token: $token, operator: $operator, productId: $productId, file: $file)
  }
`;

const DELETE_PRODUCT_IMAGE_MUTATION = gql`
  mutation deleteProductImage($token: String!, $operator: ID!, $productId: ID!, $imageUrl: String!) {
    deleteProductImage(token: $token, operator: $operator, productId: $productId, imageUrl: $imageUrl)
  }
`;

const BULK_UPDATE_OPTIONS_MUTATION = gql`
  mutation bulkUpdateOptions($token: String!, $operator: ID!, $updates: [BulkOptionUpdateInput!]!) {
    bulkUpdateOptions(token: $token, operator: $operator, updates: $updates)
  }
`;

export default function OperatorProductsScreen() {
  const [token, setToken] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const colorScheme = useColorScheme();
  const navigation = useNavigation();
  const scrollViewRef = useRef(null);

  // Bulk options state
  const [showBulkOptionsModal, setShowBulkOptionsModal] = useState(false);
  const [selectedOptionGroup, setSelectedOptionGroup] = useState(null);
  const [bulkEditPrices, setBulkEditPrices] = useState([]);
  const [bulkEditTitle, setBulkEditTitle] = useState("");
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    id: "",
    category: "",
    title: "",
    subtitle: "",
    published: true,
    available: true,
    variants: ["Default"],
    volumes: [""],
    allergens: [[]], // Array of arrays - one allergen array per variant
    prices: ["10.00"],
    order: "",
    options: [],
  });

  const { loading, error, data, refetch } = useQuery(PRODUCTS_AND_CATEGORIES_QUERY);

  useEffect(() => {
    async function fetchData() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}

      try {
        const storedAdminToken = await AsyncStorage.getItem("adminToken");
        setAdminToken(storedAdminToken);
      } catch (ex) {}

      try {
        const storedId = await AsyncStorage.getItem("operatorId");
        setOperatorId(storedId);
      } catch (ex) {}
    }
    fetchData();
  }, []);

  const [createProduct] = useMutation(CREATE_PRODUCT_MUTATION);
  const [updateProduct] = useMutation(UPDATE_PRODUCT_MUTATION);
  const [deleteProduct] = useMutation(DELETE_PRODUCT_MUTATION);
  const [uploadProductImage] = useMutation(UPLOAD_PRODUCT_IMAGE_MUTATION);
  const [deleteProductImage] = useMutation(DELETE_PRODUCT_IMAGE_MUTATION);
  const [bulkUpdateOptions] = useMutation(BULK_UPDATE_OPTIONS_MUTATION);

  const resetForm = () => {
    setFormData({
      id: "",
      category: "",
      title: "",
      subtitle: "",
      published: true,
      available: true,
      variants: ["Default"],
      volumes: [""],
      allergens: [[]],
      prices: ["10.00"],
      order: "",
      options: [],
      imageUrls: [], // Add image URLs to form data
    });
    setShowCreateForm(false);
    setEditingProduct(null);
  };

  const addVariant = () => {
    const newOptions = formData.options.map((option) => ({
      ...option,
      options: option.options.map((choice) => ({
        ...choice,
        prices: [...choice.prices, "0.00"],
      })),
    }));

    setFormData({
      ...formData,
      variants: [...formData.variants, ""],
      volumes: [...formData.volumes, ""],
      allergens: [...formData.allergens, []], // Add empty allergen array for new variant
      prices: [...formData.prices, "10.00"],
      options: newOptions,
    });
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 1) {
      Alert.alert(localization.t("Error"), localization.t("Must have at least one variant"));
      return;
    }

    const newVariants = formData.variants.filter((_, i) => i !== index);
    const newVolumes = formData.volumes.filter((_, i) => i !== index);
    const newAllergens = formData.allergens.filter((_, i) => i !== index);
    const newPrices = formData.prices.filter((_, i) => i !== index);

    const newOptions = formData.options.map((option) => ({
      ...option,
      options: option.options.map((choice) => ({
        ...choice,
        prices: choice.prices.filter((_, i) => i !== index),
      })),
    }));

    setFormData({
      ...formData,
      variants: newVariants,
      volumes: newVolumes,
      allergens: newAllergens,
      prices: newPrices,
      options: newOptions,
    });
  };

  const updateVariant = (index, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = value;
    setFormData({ ...formData, variants: newVariants });
  };

  const updateVolume = (index, value) => {
    const newVolumes = [...formData.volumes];
    newVolumes[index] = value;
    setFormData({ ...formData, volumes: newVolumes });
  };

  const updatePrice = (index, value) => {
    const newPrices = [...formData.prices];
    newPrices[index] = value;
    setFormData({ ...formData, prices: newPrices });
  };

  const updateAllergens = (index, value) => {
    const newAllergens = [...formData.allergens];
    // Split by comma and trim whitespace, filter out empty strings
    newAllergens[index] = value
      .split(",")
      .map((a) => a.trim())
      .filter((a) => a.length > 0);
    setFormData({ ...formData, allergens: newAllergens });
  };

  const getAllergensString = (index) => {
    const allergens = formData.allergens[index] || [];
    return allergens.join(", ");
  };

  // Options management
  const addOption = () => {
    const newOption = {
      id: `option-${Date.now()}`,
      type: "conf",
      title: "",
      required: false,
      options: [
        {
          id: `choice-${Date.now()}`,
          title: "",
          prices: formData.variants.map(() => "0.00"),
        },
      ],
    };
    setFormData({
      ...formData,
      options: [...formData.options, newOption],
    });
  };

  const removeOption = (optionIndex) => {
    const newOptions = formData.options.filter((_, i) => i !== optionIndex);
    setFormData({ ...formData, options: newOptions });
  };

  const updateOption = (optionIndex, field, value) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const addOptionChoice = (optionIndex) => {
    const newOptions = [...formData.options];
    const newChoice = {
      id: `choice-${Date.now()}`,
      title: "",
      prices: formData.variants.map(() => "0.00"),
    };
    newOptions[optionIndex].options = [...newOptions[optionIndex].options, newChoice];
    setFormData({ ...formData, options: newOptions });
  };

  const removeOptionChoice = (optionIndex, choiceIndex) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].options = newOptions[optionIndex].options.filter(
      (_, i) => i !== choiceIndex
    );
    setFormData({ ...formData, options: newOptions });
  };

  const updateOptionChoice = (optionIndex, choiceIndex, field, value) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].options[choiceIndex] = {
      ...newOptions[optionIndex].options[choiceIndex],
      [field]: value,
    };
    setFormData({ ...formData, options: newOptions });
  };

  const updateOptionChoicePrice = (optionIndex, choiceIndex, priceIndex, value) => {
    const newOptions = [...formData.options];
    const newPrices = [...newOptions[optionIndex].options[choiceIndex].prices];
    newPrices[priceIndex] = value;
    newOptions[optionIndex].options[choiceIndex].prices = newPrices;
    setFormData({ ...formData, options: newOptions });
  };

  const submitCreateProduct = async () => {
    const productId = uuidv4();

    try {
      const prices = formData.prices.map((p) => parseFloat(p) || 0);
      // Default empty variants to their corresponding volume value
      const variants = formData.variants.map((v, i) =>
        v.trim() || formData.volumes[i]?.trim() || ""
      ).filter((v) => v);
      const volumes = formData.volumes.filter((v) => v.trim());

      if (variants.length === 0) {
        Alert.alert(localization.t("Error"), localization.t("At least one variant is required"));
        return;
      }

      if (prices.length !== variants.length) {
        Alert.alert(localization.t("Error"), localization.t("Number of prices must match number of variants"));
        return;
      }

      if (volumes.length !== variants.length) {
        Alert.alert(localization.t("Error"), localization.t("Number of volumes must match number of variants"));
        return;
      }

      // Format options for GraphQL
      const formattedOptions = formData.options.map((option) => ({
        id: option.id,
        type: option.type,
        title: option.title,
        required: option.required,
        options: option.options.map((choice) => ({
          id: choice.id,
          title: choice.title,
          prices: choice.prices.map((p) => parseFloat(p) || 0),
        })),
      }));

      // Ensure allergens array matches variants length
      const allergens = formData.allergens.slice(0, variants.length);
      while (allergens.length < variants.length) {
        allergens.push([]);
      }

      await createProduct({
        variables: {
          token: adminToken,
          operator: operatorId,
          id: productId,
          category: formData.category,
          title: formData.title,
          subtitle: formData.subtitle || "",
          imageUrls: [],
          published: formData.published,
          available: formData.available,
          variants: variants,
          volumes: volumes,
          capabilities: ["dine-in", "takeaway"],
          capabilityData: [],
          allergens: allergens,
          prices: prices,
          options: formattedOptions.length > 0 ? formattedOptions : [],
          order: formData.order ? parseInt(formData.order) : 0,
        },
      });

      // Upload images if any were added
      if (formData.imageUrls && formData.imageUrls.length > 0) {
        for (const imageData of formData.imageUrls) {
          try {
            await uploadProductImage({
              variables: {
                token: adminToken,
                operator: operatorId,
                productId: productId,
                file: {
                  uri: imageData,
                },
              },
            });
          } catch (imageError) {
            console.error("Error uploading image:", imageError);
          }
        }
      }

      resetForm();
      refetch();
      Alert.alert(localization.t("Success"), localization.t("Product created successfully"));
    } catch (error) {
      Alert.alert(localization.t("Error"), error.message || localization.t("Failed to create product"));
    }
  };

  const handleCreateProduct = () => {
    if (!formData.category || !formData.title) {
      Alert.alert(localization.t("Error"), localization.t("Please fill in required fields (Category, Title)"));
      return;
    }

    if (!formData.imageUrls || formData.imageUrls.length === 0) {
      Alert.alert(
        localization.t("No Images"),
        localization.t("This product has no images. Are you sure you want to create it without images?"),
        [
          { text: localization.t("Cancel"), style: "cancel" },
          { text: localization.t("Create Anyway"), onPress: () => submitCreateProduct() },
        ]
      );
      return;
    }

    submitCreateProduct();
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      const updateData = {};
      if (formData.title) updateData.title = formData.title;
      if (formData.subtitle) updateData.subtitle = formData.subtitle;
      if (formData.category) updateData.category = formData.category;
      if (formData.order) updateData.order = parseInt(formData.order);
      if (formData.variants.length > 0) {
        updateData.variants = formData.variants.filter((v) => v.trim());
      }
      if (formData.volumes.length > 0) {
        updateData.volumes = formData.volumes.filter((v) => v.trim());
      }
      if (formData.prices.length > 0) {
        updateData.prices = formData.prices.map((p) => parseFloat(p) || 0);
      }
      // Always include allergens in update
      if (formData.allergens.length > 0) {
        updateData.allergens = formData.allergens;
      }
      if (formData.options.length > 0) {
        updateData.options = formData.options.map((option) => ({
          id: option.id,
          type: option.type,
          title: option.title,
          required: option.required,
          options: option.options.map((choice) => ({
            id: choice.id,
            title: choice.title,
            prices: choice.prices.map((p) => parseFloat(p) || 0),
          })),
        }));
      }
      updateData.published = formData.published;
      updateData.available = formData.available;

      await updateProduct({
        variables: {
          token: adminToken,
          operator: operatorId,
          id: editingProduct.id,
          ...updateData,
        },
      });
      
      // Upload new images if any were added
      if (formData.imageUrls && formData.imageUrls.length > 0) {
        for (const imageData of formData.imageUrls) {
          try {
            await uploadProductImage({
              variables: {
                token: adminToken,
                operator: operatorId,
                productId: editingProduct.id,
                file: {
                  uri: imageData,
                },
              },
            });
          } catch (imageError) {
            console.error("Error uploading image:", imageError);
          }
        }
      }
      
      resetForm();
      refetch();
      Alert.alert(localization.t("Success"), localization.t("Product updated successfully"));
    } catch (error) {
      Alert.alert(localization.t("Error"), error.message || localization.t("Failed to update product"));
    }
  };

  const handleDeleteProduct = async (id, title) => {
    Alert.alert(localization.t("Delete Product"), `${localization.t("Are you sure you want to delete")} "${title}"?`, [
      { text: localization.t("Cancel"), style: "cancel" },
      {
        text: localization.t("Delete"),
        style: "destructive",
        onPress: async () => {
          try {
            await deleteProduct({
              variables: {
                token: adminToken,
                operator: operatorId,
                id,
              },
            });
            refetch();
            Alert.alert(localization.t("Success"), localization.t("Product deleted successfully"));
          } catch (error) {
            Alert.alert(localization.t("Error"), error.message || localization.t("Failed to delete product"));
          }
        },
      },
    ]);
  };

  const handleUploadImage = async (productId) => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert(localization.t("Permission Required"), localization.t("Permission to access media library is required!"));
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        quality: 0.8,
        base64: true,
      });

      if (!result.canceled && result.assets[0]) {
        const file = result.assets[0];
        
        // If productId is provided, upload immediately (for existing products)
        if (productId) {
          await uploadProductImage({
            variables: {
              token: adminToken,
              operator: operatorId,
              productId,
              file: {
                uri: `data:image/jpeg;base64,${file.base64}`,
              },
            },
          });
          refetch();
          Alert.alert(localization.t("Success"), localization.t("Image uploaded successfully"));
        } else {
          // For new products, add to form data
          const imageData = `data:image/jpeg;base64,${file.base64}`;
          setFormData({
            ...formData,
            imageUrls: [...(formData.imageUrls || []), imageData],
          });
        }
      }
    } catch (error) {
      Alert.alert(localization.t("Error"), error.message || localization.t("Failed to upload image"));
    }
  };

  const handleRemoveImageFromForm = (index) => {
    const newImageUrls = formData.imageUrls.filter((_, i) => i !== index);
    setFormData({ ...formData, imageUrls: newImageUrls });
  };

  const handleDeleteImage = async (productId, imageUrl) => {
    Alert.alert(localization.t("Delete Image"), localization.t("Are you sure you want to delete this image?"), [
      { text: localization.t("Cancel"), style: "cancel" },
      {
        text: localization.t("Delete"),
        style: "destructive",
        onPress: async () => {
          try {
            await deleteProductImage({
              variables: {
                token: adminToken,
                operator: operatorId,
                productId,
                imageUrl,
              },
            });
            refetch();
            Alert.alert(localization.t("Success"), localization.t("Image deleted successfully"));
          } catch (error) {
            Alert.alert(localization.t("Error"), error.message || localization.t("Failed to delete image"));
          }
        },
      },
    ]);
  };

  // Group options across all products by (optionTitle, choiceTitle, type, prices)
  const getGroupedOptions = () => {
    const groups = {};

    products.forEach((product) => {
      if (!product.options) return;

      product.options.forEach((option) => {
        option.options?.forEach((choice) => {
          // Create a key from option title, choice title, type, and prices
          const pricesKey = choice.prices.map((p) => p.toFixed(2)).join(",");
          const groupKey = `${option.title}|${choice.title}|${option.type}|${pricesKey}`;

          if (!groups[groupKey]) {
            groups[groupKey] = {
              optionTitle: option.title,
              choiceTitle: choice.title,
              type: option.type,
              prices: [...choice.prices],
              products: [],
            };
          }

          groups[groupKey].products.push({
            productId: product.id,
            productTitle: product.title,
            optionId: option.id,
            choiceId: choice.id,
          });
        });
      });
    });

    // Convert to array and sort by number of products (descending)
    return Object.values(groups)
      .filter((g) => g.products.length > 1) // Only show options that appear in multiple products
      .sort((a, b) => b.products.length - a.products.length);
  };

  const openBulkEditModal = (group) => {
    setSelectedOptionGroup(group);
    setBulkEditPrices(group.prices.map((p) => p.toString()));
    setBulkEditTitle(group.choiceTitle);
    setShowBulkOptionsModal(true);
  };

  const handleBulkUpdate = async () => {
    if (!selectedOptionGroup) return;

    setIsBulkUpdating(true);

    try {
      // Build updates array for the bulk mutation
      const updates = selectedOptionGroup.products.map((item) => ({
        productId: item.productId,
        optionId: item.optionId,
        choiceId: item.choiceId,
        title: bulkEditTitle,
        prices: bulkEditPrices.map((p) => parseFloat(p) || 0),
      }));

      // Count unique products
      const uniqueProducts = new Set(updates.map((u) => u.productId)).size;

      // Single API call to update all options
      await bulkUpdateOptions({
        variables: {
          token: adminToken,
          operator: operatorId,
          updates,
        },
      });

      setShowBulkOptionsModal(false);
      setSelectedOptionGroup(null);
      refetch();
      Alert.alert(
        localization.t("Success"),
        `Updated ${updates.length} option(s) across ${uniqueProducts} product(s)`
      );
    } catch (error) {
      Alert.alert(localization.t("Error"), error.message || localization.t("Failed to update options"));
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const startEditing = (product) => {
    setEditingProduct(product);
    const variants = product.variants && product.variants.length > 0 ? product.variants : ["Default"];
    const prices = product.prices.map((p) => p.toString());
    // Ensure volumes array has the same length as variants
    let volumes = product.volumes || [];
    if (volumes.length < variants.length) {
      // Pad with empty strings if volumes array is shorter
      volumes = [...volumes, ...Array(variants.length - volumes.length).fill("")];
    } else if (volumes.length > variants.length) {
      // Trim if volumes array is longer
      volumes = volumes.slice(0, variants.length);
    }

    // Ensure allergens array has the same length as variants
    let allergens = product.allergens || [];
    if (allergens.length < variants.length) {
      // Pad with empty arrays if allergens array is shorter
      allergens = [...allergens, ...Array(variants.length - allergens.length).fill([])];
    } else if (allergens.length > variants.length) {
      // Trim if allergens array is longer
      allergens = allergens.slice(0, variants.length);
    }
    // Ensure each allergen entry is an array (in case of null values)
    allergens = allergens.map((a) => a || []);

    // Format options for editing (convert prices to strings)
    const formattedOptions = (product.options || []).map((option) => ({
      ...option,
      options: option.options.map((choice) => ({
        ...choice,
        prices: choice.prices.map((p) => p.toString()),
      })),
    }));

    setFormData({
      id: product.id,
      category: product.category,
      title: product.title,
      subtitle: product.subtitle || "",
      published: product.published,
      available: product.available,
      variants: variants,
      volumes: volumes,
      allergens: allergens,
      prices: prices,
      order: product.order?.toString() || "",
      options: formattedOptions,
      imageUrls: [], // New images to upload (empty when editing)
    });
    setShowCreateForm(true);
    // Scroll to top to show the edit form
    setTimeout(() => {
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }, 100);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Error loading data: {error.message}</Text>
      </View>
    );
  }

  const categories = data?.categories || [];
  const products = data?.products || [];
  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter((p) => p.category === selectedCategory);

  return (
    <ScrollView ref={scrollViewRef} style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Products Management</Text>
        <View style={styles.headerButtons}>
          <Pressable
            style={styles.bulkOptionsButton}
            onPress={() => setShowBulkOptionsModal(true)}
          >
            <MaterialIcons name="tune" size={24} color="white" />
            <Text style={styles.buttonText}>Bulk Options</Text>
          </Pressable>
          <Pressable
            style={styles.createButton}
            onPress={() => {
              resetForm();
              setShowCreateForm(!showCreateForm);
            }}
          >
            <MaterialIcons name="add" size={24} color="white" />
            <Text style={styles.buttonText}>{showCreateForm ? "Cancel" : "New Product"}</Text>
          </Pressable>
        </View>
      </View>

      {showCreateForm && (
        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>
            {editingProduct ? "Edit Product" : "Create New Product"}
          </Text>

          <TextInput
            style={styles.input}
            placeholder="Title *"
            placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
            value={formData.title}
            onChangeText={(text) => setFormData({ ...formData, title: text })}
          />

          <TextInput
            style={styles.input}
            placeholder="Subtitle"
            placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
            value={formData.subtitle}
            onChangeText={(text) => setFormData({ ...formData, subtitle: text })}
          />

          <View style={styles.pickerContainer}>
            <Text style={styles.label}>Category *</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {categories.map((cat) => (
                <Pressable
                  key={cat.id}
                  style={[
                    styles.categoryChip,
                    formData.category === cat.id && styles.categoryChipSelected,
                  ]}
                  onPress={() => setFormData({ ...formData, category: cat.id })}
                >
                  <Text
                    style={[
                      styles.categoryChipText,
                      formData.category === cat.id && styles.categoryChipTextSelected,
                    ]}
                  >
                    {cat.name}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>

          <View style={styles.variantsSection}>
            <View style={styles.sectionHeader}>
              <Text style={styles.label}>Variants, Volumes & Prices *</Text>
              <Pressable style={styles.addButton} onPress={addVariant}>
                <MaterialIcons name="add" size={20} color="white" />
                <Text style={styles.addButtonText}>Add Variant</Text>
              </Pressable>
            </View>
            <Text style={styles.helperText}>
              Each variant must have a volume and price. E.g., "Small" with "250ml" at "8.99"
            </Text>
            {formData.variants.map((variant, index) => (
              <View key={index} style={styles.variantRow}>
                <View style={styles.variantInputsContainer}>
                  <TextInput
                    style={[styles.input, styles.variantInput]}
                    placeholder={`Variant ${index + 1} (e.g., Small)`}
                    placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                    value={variant}
                    onChangeText={(text) => updateVariant(index, text)}
                  />
                  <TextInput
                    style={[styles.input, styles.volumeInput]}
                    placeholder={localization.t("Volume (e.g., 250ml)")}
                    placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                    value={formData.volumes[index]}
                    onChangeText={(text) => updateVolume(index, text)}
                  />
                  <TextInput
                    style={[styles.input, styles.priceInput]}
                    placeholder={localization.t("Price")}
                    placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                    value={formData.prices[index]}
                    onChangeText={(text) => updatePrice(index, text)}
                    keyboardType="decimal-pad"
                  />
                </View>
                {formData.variants.length > 1 && (
                  <Pressable
                    style={styles.removeButton}
                    onPress={() => removeVariant(index)}
                  >
                    <MaterialIcons name="close" size={20} color="#FF3B30" />
                  </Pressable>
                )}
              </View>
            ))}
          </View>

          <View style={styles.allergensSection}>
            <Text style={styles.label}>{localization.t("Allergens (optional)")}</Text>
            <Text style={styles.helperText}>
              Enter allergens for each variant, separated by commas. E.g., "Gluten, Dairy, Nuts"
            </Text>
            {formData.variants.map((variant, index) => (
              <View key={index} style={styles.allergenRow}>
                <Text style={styles.allergenVariantLabel}>
                  {variant || `Variant ${index + 1}`}:
                </Text>
                <TextInput
                  style={[styles.input, styles.allergenInput]}
                  placeholder={localization.t("e.g., Gluten, Dairy, Nuts")}
                  placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                  value={getAllergensString(index)}
                  onChangeText={(text) => updateAllergens(index, text)}
                />
              </View>
            ))}
          </View>

          <TextInput
            style={styles.input}
            placeholder="Order (optional)"
            placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
            value={formData.order}
            onChangeText={(text) => setFormData({ ...formData, order: text })}
            keyboardType="numeric"
          />

          <View style={styles.toggleRow}>
            <Text style={styles.label}>Published:</Text>
            <Pressable
              style={[styles.toggle, formData.published && styles.toggleActive]}
              onPress={() => setFormData({ ...formData, published: !formData.published })}
            >
              <Text style={styles.toggleText}>{formData.published ? "Yes" : "No"}</Text>
            </Pressable>
          </View>

          <View style={styles.toggleRow}>
            <Text style={styles.label}>Available:</Text>
            <Pressable
              style={[styles.toggle, formData.available && styles.toggleActive]}
              onPress={() => setFormData({ ...formData, available: !formData.available })}
            >
              <Text style={styles.toggleText}>{formData.available ? "Yes" : "No"}</Text>
            </Pressable>
          </View>

          <View style={styles.optionsSection}>
            <View style={styles.sectionHeader}>
              <Text style={styles.label}>{localization.t("Product Options (optional)")}</Text>
              <Pressable style={styles.addButton} onPress={addOption}>
                <MaterialIcons name="add" size={20} color="white" />
                <Text style={styles.addButtonText}>Add Option</Text>
              </Pressable>
            </View>
            <Text style={styles.helperText}>
              Options are extras/customizations like size, toppings, etc. Each choice has prices per variant.
            </Text>

            {formData.options.map((option, optionIndex) => (
              <View key={optionIndex} style={styles.optionCard}>
                <View style={styles.optionHeader}>
                  <Text style={styles.optionTitle}>Option {optionIndex + 1}</Text>
                  <Pressable
                    style={styles.iconButton}
                    onPress={() => removeOption(optionIndex)}
                  >
                    <MaterialIcons name="delete" size={20} color="#FF3B30" />
                  </Pressable>
                </View>

                <TextInput
                  style={styles.input}
                  placeholder="Option Title (e.g., Choose Size)"
                  placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                  value={option.title}
                  onChangeText={(text) => updateOption(optionIndex, "title", text)}
                />

                <View style={styles.optionTypeRow}>
                  <Text style={styles.label}>Type:</Text>
                  <View style={styles.typeButtons}>
                    <Pressable
                      style={[
                        styles.typeButton,
                        option.type === "conf" && styles.typeButtonActive,
                      ]}
                      onPress={() => updateOption(optionIndex, "type", "conf")}
                    >
                      <Text
                        style={[
                          styles.typeButtonText,
                          option.type === "conf" && styles.typeButtonTextActive,
                        ]}
                      >
                        Conf (single)
                      </Text>
                    </Pressable>
                    <Pressable
                      style={[
                        styles.typeButton,
                        option.type === "add" && styles.typeButtonActive,
                      ]}
                      onPress={() => updateOption(optionIndex, "type", "add")}
                    >
                      <Text
                        style={[
                          styles.typeButtonText,
                          option.type === "add" && styles.typeButtonTextActive,
                        ]}
                      >
                        Add (multiple)
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <View style={styles.toggleRow}>
                  <Text style={styles.label}>Required:</Text>
                  <Pressable
                    style={[styles.toggle, option.required && styles.toggleActive]}
                    onPress={() => updateOption(optionIndex, "required", !option.required)}
                  >
                    <Text style={styles.toggleText}>{option.required ? "Yes" : "No"}</Text>
                  </Pressable>
                </View>

                <View style={styles.choicesSection}>
                  <View style={styles.sectionHeader}>
                    <Text style={styles.label}>Choices</Text>
                    <Pressable
                      style={styles.addButton}
                      onPress={() => addOptionChoice(optionIndex)}
                    >
                      <MaterialIcons name="add" size={16} color="white" />
                      <Text style={styles.addButtonText}>Add Choice</Text>
                    </Pressable>
                  </View>

                  {option.options.map((choice, choiceIndex) => (
                    <View key={choiceIndex} style={styles.choiceCard}>
                      <View style={styles.choiceHeader}>
                        <TextInput
                          style={[styles.input, styles.choiceTitleInput]}
                          placeholder={`Choice ${choiceIndex + 1} (e.g., Large)`}
                          placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                          value={choice.title}
                          onChangeText={(text) =>
                            updateOptionChoice(optionIndex, choiceIndex, "title", text)
                          }
                        />
                        <Pressable
                          style={styles.iconButton}
                          onPress={() => removeOptionChoice(optionIndex, choiceIndex)}
                        >
                          <MaterialIcons name="close" size={16} color="#FF3B30" />
                        </Pressable>
                      </View>

                      <Text style={styles.pricesLabel}>
                        Prices per variant (additional cost):
                      </Text>
                      <View style={styles.choicePricesRow}>
                        {formData.variants.map((variant, priceIndex) => (
                          <View key={priceIndex} style={styles.choicePriceItem}>
                            <Text style={styles.variantLabel}>
                              {variant || `Variant ${priceIndex + 1}`}
                            </Text>
                            <TextInput
                              style={[styles.input, styles.choicePriceInput]}
                              placeholder="0.00"
                              placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
                              value={choice.prices[priceIndex]}
                              onChangeText={(text) =>
                                updateOptionChoicePrice(optionIndex, choiceIndex, priceIndex, text)
                              }
                              keyboardType="decimal-pad"
                            />
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            ))}

            {formData.options.length === 0 && (
              <Text style={styles.helperText}>
                No options added. Options allow customers to customize their order (e.g., size, toppings).
              </Text>
            )}
          </View>

          {/* Image Management Section */}
          <View style={styles.imagesFormSection}>
            <View style={styles.sectionHeader}>
              <Text style={styles.label}>Product Images (optional)</Text>
              <Pressable style={styles.addButton} onPress={() => handleUploadImage(null)}>
                <MaterialIcons name="add-a-photo" size={20} color="white" />
                <Text style={styles.addButtonText}>Add Image</Text>
              </Pressable>
            </View>
            <Text style={styles.helperText}>
              Add product images. Images will be uploaded when you {editingProduct ? "update" : "create"} the product.
            </Text>
            
            {formData.imageUrls && formData.imageUrls.length > 0 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.imagesPreview}>
                {formData.imageUrls.map((imageData, index) => (
                  <View key={index} style={styles.imageWrapper}>
                    <Image source={{ uri: imageData, cache: 'default' }} style={styles.productImage} />
                    <Pressable
                      style={styles.deleteImageButton}
                      onPress={() => handleRemoveImageFromForm(index)}
                    >
                      <MaterialIcons name="close" size={16} color="white" />
                    </Pressable>
                  </View>
                ))}
              </ScrollView>
            )}
            
            {editingProduct && editingProduct.imageUrls && editingProduct.imageUrls.length > 0 && (
              <View>
                <Text style={styles.label}>Existing Images</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.imagesPreview}>
                  {editingProduct.imageUrls.map((url, index) => (
                    <View key={index} style={styles.imageWrapper}>
                      <Image source={{ uri: getProductImageUrl(url), cache: 'default' }} style={styles.productImage} />
                      <Pressable
                        style={styles.deleteImageButton}
                        onPress={() => handleDeleteImage(editingProduct.id, url)}
                      >
                        <MaterialIcons name="close" size={16} color="white" />
                      </Pressable>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>

          <Pressable
            style={styles.submitButton}
            onPress={editingProduct ? handleUpdateProduct : handleCreateProduct}
          >
            <Text style={styles.buttonText}>
              {editingProduct ? "Update Product" : "Create Product"}
            </Text>
          </Pressable>
        </View>
      )}

      <View style={styles.filterSection}>
        <Text style={styles.label}>Filter by Category:</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <Pressable
            style={[styles.filterChip, selectedCategory === "all" && styles.filterChipSelected]}
            onPress={() => setSelectedCategory("all")}
          >
            <Text
              style={[
                styles.filterChipText,
                selectedCategory === "all" && styles.filterChipTextSelected,
              ]}
            >
              All ({products.length})
            </Text>
          </Pressable>
          {categories.map((cat) => {
            const count = products.filter((p) => p.category === cat.id).length;
            return (
              <Pressable
                key={cat.id}
                style={[
                  styles.filterChip,
                  selectedCategory === cat.id && styles.filterChipSelected,
                ]}
                onPress={() => setSelectedCategory(cat.id)}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    selectedCategory === cat.id && styles.filterChipTextSelected,
                  ]}
                >
                  {cat.name} ({count})
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.productsSection}>
        <Text style={styles.sectionTitle}>Products ({filteredProducts.length})</Text>
        {filteredProducts.map((product) => (
          <View key={product.id} style={styles.productItem}>
            <View style={styles.productHeader}>
              <View style={styles.productInfo}>
                <Text style={styles.productTitle}>{product.title}</Text>
                {product.subtitle && (
                  <Text style={styles.productSubtitle}>{product.subtitle}</Text>
                )}
                <Text style={styles.productMeta}>
                  ID: {product.id} • Category: {product.category}
                </Text>
                <Text style={styles.productMeta}>
                  Variants: {product.variants?.join(", ") || "None"}
                  {product.volumes?.length > 0 && ` • Volumes: ${product.volumes.join(", ")}`}
                </Text>
                <Text style={styles.productMeta}>
                  Prices: {product.prices?.map((p) => p.toFixed(2)).join(", ")}
                </Text>
                {product.allergens && product.allergens.some(a => a && a.length > 0) && (
                  <Text style={styles.productMeta}>
                    Allergens: {product.variants?.map((variant, idx) => {
                      const allergens = product.allergens[idx];
                      if (allergens && allergens.length > 0) {
                        return `${variant}: ${allergens.join(", ")}`;
                      }
                      return null;
                    }).filter(Boolean).join(" | ")}
                  </Text>
                )}
                <View style={styles.statusRow}>
                  <View style={[styles.badge, product.published && styles.badgeSuccess]}>
                    <Text style={styles.badgeText}>
                      {product.published ? localization.t("Published") : "Draft"}
                    </Text>
                  </View>
                  <View style={[styles.badge, product.available && styles.badgeSuccess]}>
                    <Text style={styles.badgeText}>
                      {product.available ? localization.t("Available") : "Unavailable"}
                    </Text>
                  </View>
                </View>
              </View>
              <View style={styles.actionButtons}>
                <Pressable
                  style={styles.iconButton}
                  onPress={() => startEditing(product)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <MaterialIcons name="edit" size={24} color="#007AFF" />
                </Pressable>
                <Pressable
                  style={styles.iconButton}
                  onPress={() => handleDeleteProduct(product.id, product.title)}
                >
                  <MaterialIcons name="delete" size={24} color="#FF3B30" />
                </Pressable>
              </View>
            </View>

            {product.options && product.options.length > 0 && (
              <View style={styles.optionsDisplaySection}>
                <Text style={styles.imagesTitle}>
                  Options ({product.options.length})
                </Text>
                {product.options.map((opt, idx) => (
                  <Text key={idx} style={styles.optionDisplayText}>
                    • {opt.title} ({opt.type}) - {opt.options?.length || 0} choice(s)
                  </Text>
                ))}
              </View>
            )}

            <View style={styles.imagesSection}>
              <Text style={styles.imagesTitle}>Images ({product.imageUrls?.length || 0})</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {product.imageUrls?.map((url, index) => (
                  <View key={index} style={styles.imageWrapper}>
                    <Image source={{ uri: getProductImageUrl(url), cache: 'default' }} style={styles.productImage} />
                    <Pressable
                      style={styles.deleteImageButton}
                      onPress={() => handleDeleteImage(product.id, url)}
                    >
                      <MaterialIcons name="close" size={16} color="white" />
                    </Pressable>
                  </View>
                ))}
                <Pressable
                  style={styles.addImageButton}
                  onPress={() => handleUploadImage(product.id)}
                >
                  <MaterialIcons name="add-a-photo" size={32} color="#007AFF" />
                </Pressable>
              </ScrollView>
            </View>
          </View>
        ))}
      </View>

      {/* Bulk Options Modal */}
      <Modal
        visible={showBulkOptionsModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowBulkOptionsModal(false);
          setSelectedOptionGroup(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {selectedOptionGroup ? "Edit Option" : "Bulk Options Manager"}
              </Text>
              <Pressable
                onPress={() => {
                  setShowBulkOptionsModal(false);
                  setSelectedOptionGroup(null);
                }}
              >
                <MaterialIcons name="close" size={24} color="#666" />
              </Pressable>
            </View>

            <ScrollView style={styles.modalBody}>
              {!selectedOptionGroup ? (
                <>
                  <Text style={styles.modalDescription}>
                    Options that appear in multiple products with the same name, type, and prices.
                    Click on an option to edit it across all products at once.
                  </Text>
                  {getGroupedOptions().length === 0 ? (
                    <Text style={styles.noOptionsText}>
                      No shared options found across products.
                    </Text>
                  ) : (
                    getGroupedOptions().map((group, index) => (
                      <Pressable
                        key={index}
                        style={styles.optionGroupCard}
                        onPress={() => openBulkEditModal(group)}
                      >
                        <View style={styles.optionGroupHeader}>
                          <Text style={styles.optionGroupTitle}>{group.choiceTitle}</Text>
                          <View style={styles.optionGroupBadge}>
                            <Text style={styles.optionGroupBadgeText}>
                              {group.products.length} products
                            </Text>
                          </View>
                        </View>
                        <Text style={styles.optionGroupMeta}>
                          Option: {group.optionTitle} ({group.type})
                        </Text>
                        <Text style={styles.optionGroupMeta}>
                          Prices: {group.prices.map((p) => p.toFixed(2)).join(", ")}
                        </Text>
                        <Text style={styles.optionGroupProducts}>
                          Products: {group.products.map((p) => p.productTitle).slice(0, 3).join(", ")}
                          {group.products.length > 3 && ` +${group.products.length - 3} more`}
                        </Text>
                      </Pressable>
                    ))
                  )}
                </>
              ) : (
                <>
                  <Text style={styles.modalDescription}>
                    Editing "{selectedOptionGroup.choiceTitle}" across {selectedOptionGroup.products.length} products.
                  </Text>

                  <Text style={styles.bulkEditLabel}>Choice Title:</Text>
                  <TextInput
                    style={styles.input}
                    value={bulkEditTitle}
                    onChangeText={setBulkEditTitle}
                    placeholder="Choice title"
                    placeholderTextColor="#999"
                  />

                  <Text style={styles.bulkEditLabel}>Prices (per variant):</Text>
                  {bulkEditPrices.map((price, index) => (
                    <View key={index} style={styles.bulkPriceRow}>
                      <Text style={styles.bulkPriceLabel}>Variant {index + 1}:</Text>
                      <TextInput
                        style={[styles.input, styles.bulkPriceInput]}
                        value={price}
                        onChangeText={(text) => {
                          const newPrices = [...bulkEditPrices];
                          newPrices[index] = text;
                          setBulkEditPrices(newPrices);
                        }}
                        placeholder="0.00"
                        placeholderTextColor="#999"
                        keyboardType="decimal-pad"
                      />
                    </View>
                  ))}

                  <Text style={styles.affectedProductsTitle}>Affected Products:</Text>
                  {selectedOptionGroup.products.map((item, index) => (
                    <Text key={index} style={styles.affectedProductItem}>
                      {item.productTitle}
                    </Text>
                  ))}
                </>
              )}
            </ScrollView>

            <View style={styles.modalFooter}>
              {selectedOptionGroup ? (
                <>
                  <Pressable
                    style={styles.modalCancelButton}
                    onPress={() => setSelectedOptionGroup(null)}
                  >
                    <Text style={styles.modalCancelButtonText}>Back</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.modalSubmitButton, isBulkUpdating && styles.buttonDisabled]}
                    onPress={handleBulkUpdate}
                    disabled={isBulkUpdating}
                  >
                    {isBulkUpdating ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <Text style={styles.buttonText}>
                        Update {selectedOptionGroup.products.length} Options
                      </Text>
                    )}
                  </Pressable>
                </>
              ) : (
                <Pressable
                  style={styles.modalCancelButton}
                  onPress={() => setShowBulkOptionsModal(false)}
                >
                  <Text style={styles.modalCancelButtonText}>Close</Text>
                </Pressable>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 15,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  createButton: {
    backgroundColor: "#007AFF",
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderRadius: 8,
    gap: 5,
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  formSection: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    fontSize: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
  },
  pickerContainer: {
    marginBottom: 15,
  },
  categoryChip: {
    backgroundColor: "#f0f0f0",
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
  },
  categoryChipSelected: {
    backgroundColor: "#007AFF",
  },
  categoryChipText: {
    fontSize: 14,
    color: "#333",
  },
  categoryChipTextSelected: {
    color: "white",
    fontWeight: "600",
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  toggle: {
    backgroundColor: "#f0f0f0",
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
  },
  toggleActive: {
    backgroundColor: "#34C759",
  },
  toggleText: {
    color: "white",
    fontWeight: "600",
  },
  submitButton: {
    backgroundColor: "#34C759",
    padding: 15,
    borderRadius: 8,
    alignItems: "center",
  },
  filterSection: {
    marginBottom: 20,
  },
  filterChip: {
    backgroundColor: "#f0f0f0",
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
  },
  filterChipSelected: {
    backgroundColor: "#007AFF",
  },
  filterChipText: {
    fontSize: 14,
    color: "#333",
  },
  filterChipTextSelected: {
    color: "white",
    fontWeight: "600",
  },
  productsSection: {
    marginBottom: 20,
  },
  productItem: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
  },
  productHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  productInfo: {
    flex: 1,
  },
  productTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 5,
  },
  productSubtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 5,
  },
  productMeta: {
    fontSize: 12,
    color: "#999",
    marginBottom: 8,
  },
  statusRow: {
    flexDirection: "row",
    gap: 8,
  },
  badge: {
    backgroundColor: "#8E8E93",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  badgeSuccess: {
    backgroundColor: "#34C759",
  },
  badgeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
  },
  actionButtons: {
    flexDirection: "row",
    gap: 10,
  },
  iconButton: {
    padding: 8,
  },
  imagesSection: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    paddingTop: 10,
  },
  imagesFormSection: {
    marginBottom: 15,
  },
  imagesPreview: {
    marginTop: 10,
    marginBottom: 10,
  },
  imagesTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
  },
  imageWrapper: {
    position: "relative",
    marginRight: 10,
  },
  productImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  deleteImageButton: {
    position: "absolute",
    top: -5,
    right: -5,
    backgroundColor: "#FF3B30",
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  addImageButton: {
    width: 80,
    height: 80,
    borderWidth: 2,
    borderColor: "#007AFF",
    borderStyle: "dashed",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    color: "red",
    fontSize: 16,
    textAlign: "center",
  },
  variantsSection: {
    marginBottom: 15,
  },
  volumesSection: {
    marginBottom: 15,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  addButton: {
    backgroundColor: "#007AFF",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 4,
  },
  addButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  variantRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 10,
    alignItems: "flex-start",
  },
  variantInputsContainer: {
    flex: 1,
    flexDirection: "row",
    gap: 8,
  },
  variantInput: {
    flex: 2,
    marginBottom: 0,
  },
  volumeInput: {
    flex: 1.5,
    marginBottom: 0,
  },
  priceInput: {
    flex: 1,
    marginBottom: 0,
  },
  removeButton: {
    padding: 8,
  },
  helperText: {
    fontSize: 14,
    color: "#666",
    fontStyle: "italic",
    marginBottom: 8,
  },
  optionsSection: {
    marginBottom: 15,
  },
  optionCard: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    backgroundColor: "#fafafa",
  },
  optionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  optionTypeRow: {
    marginBottom: 10,
  },
  typeButtons: {
    flexDirection: "row",
    gap: 8,
    marginTop: 8,
  },
  typeButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#ccc",
    backgroundColor: "#fff",
    alignItems: "center",
  },
  typeButtonActive: {
    backgroundColor: "#007AFF",
    borderColor: "#007AFF",
  },
  typeButtonText: {
    fontSize: 14,
    color: "#333",
  },
  typeButtonTextActive: {
    color: "white",
    fontWeight: "600",
  },
  choicesSection: {
    marginTop: 15,
  },
  choiceCard: {
    backgroundColor: "#fff",
    borderRadius: 6,
    padding: 10,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  choiceHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  choiceTitleInput: {
    flex: 1,
    marginBottom: 0,
  },
  pricesLabel: {
    fontSize: 13,
    fontWeight: "600",
    marginBottom: 8,
    color: "#555",
  },
  choicePricesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  choicePriceItem: {
    minWidth: 100,
    marginBottom: 8,
  },
  variantLabel: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  choicePriceInput: {
    marginBottom: 0,
    paddingVertical: 8,
  },
  optionsInfoSection: {
    marginTop: 15,
    padding: 12,
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  optionText: {
    fontSize: 13,
    color: "#444",
    marginTop: 4,
  },
  optionsDisplaySection: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#eee",
  },
  optionDisplayText: {
    fontSize: 13,
    color: "#666",
    marginTop: 3,
  },
  allergensSection: {
    marginBottom: 15,
  },
  allergenRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  allergenVariantLabel: {
    fontSize: 14,
    fontWeight: "500",
    minWidth: 80,
    color: "#555",
  },
  allergenInput: {
    flex: 1,
    marginBottom: 0,
  },
  // Header buttons styles
  headerButtons: {
    flexDirection: "row",
    gap: 10,
  },
  bulkOptionsButton: {
    backgroundColor: "#5856D6",
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderRadius: 8,
    gap: 5,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 12,
    width: "100%",
    maxWidth: 500,
    maxHeight: "90%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  modalBody: {
    padding: 16,
    maxHeight: 400,
  },
  modalDescription: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
    lineHeight: 20,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    gap: 10,
  },
  modalCancelButton: {
    backgroundColor: "#f0f0f0",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  modalCancelButtonText: {
    color: "#333",
    fontSize: 16,
    fontWeight: "600",
  },
  modalSubmitButton: {
    backgroundColor: "#34C759",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 120,
    alignItems: "center",
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  // Option group card styles
  optionGroupCard: {
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  optionGroupHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  optionGroupTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  optionGroupBadge: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  optionGroupBadgeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
  },
  optionGroupMeta: {
    fontSize: 13,
    color: "#666",
    marginBottom: 4,
  },
  optionGroupProducts: {
    fontSize: 12,
    color: "#999",
    marginTop: 4,
  },
  noOptionsText: {
    fontSize: 14,
    color: "#999",
    textAlign: "center",
    paddingVertical: 20,
    fontStyle: "italic",
  },
  // Bulk edit styles
  bulkEditLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
    marginTop: 12,
  },
  bulkPriceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  },
  bulkPriceLabel: {
    fontSize: 14,
    color: "#666",
    minWidth: 80,
  },
  bulkPriceInput: {
    flex: 1,
    marginBottom: 0,
  },
  affectedProductsTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8,
  },
  affectedProductItem: {
    fontSize: 13,
    color: "#666",
    paddingVertical: 4,
    paddingLeft: 8,
    borderLeftWidth: 2,
    borderLeftColor: "#007AFF",
    marginBottom: 4,
  },
});
